import { AfterViewInit, ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, OnDestroy, OnInit } from '@angular/core';
import { HttpService } from '../../services/http.service';
import { StorageService } from '../../services/storage.service';
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  Filler,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  ScatterController,
  TimeScale,
  Title,
  Tooltip,
} from 'chart.js';
import { LangChangeEvent, TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { CommonModule } from '@angular/common';
import { forkJoin, map, Subject, take, takeUntil } from 'rxjs';
import { UserAccountDTO } from '../../classes/user-account.dto';
import { ModalAddValueFormByIdComponent } from '../modals/modal-add-value-form-by-id/modal-add-value-form-by-id.component';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { IGraphConfig } from '../../interfaces/graph.interface';
import { GraphMisurationData } from './chartsMisurationData';
import { format, subYears } from 'date-fns';
import { CardWithValueComponent } from '../utility/card-with-value/card-with-value.component';
import { ICardWithValue } from '../../interfaces/card-with-value.interface';
import { DataService } from '../../services/data.service';
import { MeasurementDTO, MedicalDataDTO } from '../../classes/api-dto';
import 'chartjs-adapter-date-fns';
import { AppointmentsTableComponent } from '../appointments-table/appointments-table.component';
import { ToastrService } from 'ngx-toastr';
import { ModalWithTwoButtonsComponent } from '../modals/modal-with-two-buttons/modal-with-two-buttons.component';

export type MedicalMisurationType =
  | 'bpm'
  | 'weight'
  | 'height'
  | 'temperature'
  | 'pressure'
  | 'waistCircumference'
  | 'glycemia'
  | 'isSmoker'
  | 'bloodGroup'
  | 'hipCircumference'
  | 'wristCircumference';
export const validTypesSet = new Set<MedicalMisurationType>([
  'bpm',
  'weight',
  'height',
  'temperature',
  'pressure',
  'waistCircumference',
  'glycemia',
  'isSmoker',
  'bloodGroup',
  'hipCircumference',
  'wristCircumference',
]);
@Component({
    selector: 'app-misuration',
    imports: [TranslateModule, NzDividerModule, CommonModule, CardWithValueComponent, AppointmentsTableComponent],
    templateUrl: './misuration.component.html',
    styleUrls: ['./misuration.component.scss'],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class MisurationComponent implements OnInit, OnDestroy, AfterViewInit {
  private destroy$ = new Subject<void>();
  filtersGraph?: [{ label: string; id: string }];
  medicalCardData?: ICardWithValue[];
  activeFilterGraph!: string;
  graphsDataSubject = new Subject<{
    [key: string]: { elements: any[]; label: string };
  }>();
  graphsData$ = this.graphsDataSubject.asObservable();
  graphConfig: Array<IGraphConfig & { id: MedicalMisurationType }> = GraphMisurationData;
  graphList: Array<{ graph: Chart; id: string }> = [];
  columns!: any;
  appointmentsText: any;
  tableData: any = {};

  constructor(
    private http: HttpService,
    private storage: StorageService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private modal: NzModalService,
    private dataService: DataService,
    private translateService: TranslateService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.activeFilterGraph = 'all';
    this.listenToFiltersTranslations();
    this.getCardData();
    this.getColumns();
    this.translateService.onLangChange.subscribe(() => {
      this.getColumns();
    });
  }

  ngAfterViewInit(): void {
    this.initializeGraphs();
  }

  private listenToFiltersTranslations(): void {
    this.filtersGraph = this.translate.instant('misuration.filters_graph');
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe((translatedFilter: LangChangeEvent) => {
      this.filtersGraph = translatedFilter.translations.misuration.filters_graph;
      if (this.filtersGraph) {
        this.activeFilterGraph = this.filtersGraph[0].id;
      }
    });
  }

  private getCardData(): void {
    this.dataService.getMedicalData().subscribe((medicalData: MedicalDataDTO) => {
      if (medicalData.lastMeasurement) {
        this.createCardData(medicalData.lastMeasurement);
      }
    });
  }

  private createCardData(data: MeasurementDTO): void {
    this.medicalCardData = [
      {
        id: 'weight',
        value: data.weight ? data.weight + ' kg' : '---',
        secondValue: data.lastTimeModified ? format(new Date(data.lastTimeModified), 'dd.MM.yyyy') : '---',
        textsPointer: 'misuration.card_texts.',
        img: 'balance.svg',
      },
      {
        id: 'height',
        value: data.height ? data.height + ' cm' : '---',
        secondValue: data.lastTimeModified ? format(new Date(data.lastTimeModified), 'dd.MM.yyyy') : ' ---',
        textsPointer: 'misuration.card_texts.',
        img: 'height.svg',
      },
      {
        id: 'waistCircumference',
        value: data.waistCircumference ? data.waistCircumference + ' cm' : '---',
        secondValue: data.lastTimeModified ? format(new Date(data.lastTimeModified), 'dd.MM.yyyy') : ' ---',
        textsPointer: 'misuration.card_texts.',
        img: 'width.svg',
      },
    ];
  }

  private initializeGraphs(): void {
    /* TODO: andrebbe creato un componente separato per i grafici, nel caso in cui ce ne fosse bisogno altrove */
    Chart.register(Title, TimeScale, LinearScale, PointElement, Tooltip, Legend, ScatterController, LineController, LineElement, BarController, BarElement, Filler, CategoryScale);

    this.getGraphsData(['bpm', 'weight', 'temperature', 'glycemia', 'waistCircumference', 'pressure', 'hipCircumference', 'wristCircumference']);

    this.graphsData$.pipe(takeUntil(this.destroy$), take(1)).subscribe((graphsData) => {
      this.createGraphConfig(graphsData);
    });
  }

  getGraphsData(graphsTypes: MedicalMisurationType[]): void {
    const userData = JSON.parse(this.storage.getItem('userData')!) as UserAccountDTO;
    const today = new Date();
    const oneYearAgo = subYears(today, 1);
    const todayFormatted = format(today, 'yyyy-MM-dd');
    const oneYearAgoFormatted = format(oneYearAgo, 'yyyy-MM-dd');

    forkJoin(
      graphsTypes.map((type: MedicalMisurationType) =>
        this.http.specificMedicalStat(type, userData.individualId, oneYearAgoFormatted, todayFormatted).pipe(
          takeUntil(this.destroy$),
          map((res) => ({ type, res }))
        )
      )
    )
      .pipe(
        map((results) =>
          results.map(({ type, res }) => {
            console.log(type, res);
            let label = '-';
            let elements: any[] = [];

            if (res.lista.length > 0) {
              this.tableData[type] = this.createTableData(type, res.lista);
              elements = res.lista.map((element: any, index: number) => {
                const x = element.creationDate ? format(new Date(element.creationDate), 'yyyy-MM-dd') : index;
                if (type !== 'pressure') {
                  return { x, y: element[type] };
                } else {
                  return { x, y: [element['minValue'], element['maxValue']] };
                }
              });
              label = res.lista[res.lista.length - 1]?.[type + 'StatusLabel'] || '-';
            }

            return { type, res: { elements, label } };
          })
        )
      )
      .subscribe((results) => {
        const result: { [key: string]: { elements: any[]; label: string } } = {};
        results.forEach(({ type, res }) => {
          result[type] = res;
        });
        this.graphsDataSubject.next(result);
      });
  }

  private createGraphConfig(graphsData: { [key: string]: { elements: any[]; label: string } }): void {
    this.graphConfig.forEach((config: IGraphConfig & { id: MedicalMisurationType }) => {
      config.title = this.translate.instant(`misuration.name_graphs.${config.id}`);
    });
    this.listenToNamesGraphsTranslations();
    this.createGraphs(graphsData);
  }

  private listenToNamesGraphsTranslations(): void {
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe((event: LangChangeEvent) => {
      this.graphConfig.forEach((graphConfig: IGraphConfig) => {
        graphConfig.title = event.translations.misuration.name_graphs[graphConfig.id];
      });
    });
  }

  private createGraphs(graphsData: { [key: string]: { elements: any[]; label: string } }): void {
    this.graphConfig.forEach((graphConfig: IGraphConfig) => {
      graphsData;
      const ctx = document.getElementById(graphConfig.id) as HTMLCanvasElement;
      if (!ctx) return;

      const graphData = graphsData[graphConfig.id] || { elements: [], label: '-' };

      const graph = new Chart(ctx, {
        type: graphConfig.design.type,
        data: {
          datasets: [
            {
              label: this.translate.instant('misuration.label_graph.' + (graphData.label !== '-' ? graphData.label : graphConfig.id ? graphConfig.id : '-')),
              data: graphData.elements || [],
              backgroundColor: graphConfig.design.color,
              borderColor: graphConfig.design.borderColor,
              pointRadius: graphConfig.design.pointSize,
              fill: graphConfig.design.graphicFill,
              borderDash: [graphConfig.design.borderDash],
              borderWidth: graphConfig.design.borderWidth || undefined,
              pointBackgroundColor: graphConfig.design.pointBackgroundColor,
              borderRadius: graphConfig.design.borderRadius,
              borderSkipped: graphConfig.design.borderSkipped,
            },
          ],
        },
        options: {
          plugins: {
            legend: {
              align: 'start',
              labels: {
                usePointStyle: true,
                pointStyle: 'circle',
                boxWidth: 7,
                boxHeight: 5,
                padding: 5,
                font: { size: 14 },
              },
            },
          },
          responsive: true,
          scales: {
            x: {
              type: 'time',
              time: { unit: 'day' },
            },
            y: {
              beginAtZero: graphConfig.design.beginAtZero,
              min: graphConfig.design.minValueY,
              max: graphConfig.design.maxValueY,
            },
          },
        },
      });

      this.graphList.push({ graph, id: graphConfig.id });
      this.cdr.detectChanges();
    });
  }

  private isDate(value: any): boolean {
    if (typeof value === 'string' || value instanceof String) {
      const date = new Date(value as string);
      return !isNaN(date.getTime()) && isNaN(Number(value));
    }
    return value instanceof Date && !isNaN(value.getTime());
  }

  protected addGraphValue(graphModified: string, graph: boolean): void {
    if (validTypesSet.has(graphModified as MedicalMisurationType)) {
      const modalRef: NzModalRef<ModalAddValueFormByIdComponent> = this.modal.create({
        nzContent: ModalAddValueFormByIdComponent,
        nzClosable: true,
        nzFooter: null,
        nzData: {
          dataModal: { id: graphModified, graph: graph },
        },
      });

      modalRef.afterClose.subscribe((modifiedGraph: MedicalMisurationType): void => {
        if (modifiedGraph) {
          this.updateGraphData(modifiedGraph);
        }
      });
    }
  }

  protected filterSelected(graphSelected: string): void {
    this.activeFilterGraph = graphSelected;
    this.tableData[this.activeFilterGraph] = this.tableData[this.activeFilterGraph];
    this.cdr.detectChanges();
  }

  private updateGraphData(modifiedGraph: MedicalMisurationType): void {
    this.getGraphsData([modifiedGraph]);
    this.graphsData$.pipe(take(1)).subscribe((newGraphsData) => {
      if (Object.keys(newGraphsData).length > 0) {
        if (this.graphList.length > 0) {
          this.graphList.forEach((el: { graph: any; id: string }) => {
            if (el.id === modifiedGraph) {
              el.graph.data.datasets[0].data = newGraphsData[modifiedGraph]?.['elements'];
              el.graph.update();
              this.cdr.detectChanges();
              return;
            }
          });
          return;
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getColumns() {
    this.translateService.get('healthFolder').subscribe((texts) => {
      this.columns = texts.columns;
    });
    this.translateService.get('healthFolder').subscribe((texts) => {
      this.appointmentsText = texts.appointmentsText;
    });
  }

  private createTableData(type: string, res: any) {
    if (res) {
      const sortedArray = res.sort((a: any, b: any) => new Date(a.creationDate).getTime() - new Date(b.creationDate).getTime());

      return sortedArray.map((current: any, index: any) => {
        const previous = index > 0 ? sortedArray[index - 1] : null;
        let course = 'neutro';
        if (previous) {
          const diff = current[type] - previous[type];
          if (diff > 0) {
            course = 'positivo';
          } else if (diff < 0) {
            course = 'negativo';
          }
        }
        const creationDate = new Date(current.creationDate);
        const consultDate = creationDate.toLocaleDateString('it-IT', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        });
        const time = creationDate.toLocaleTimeString('it-IT', {
          hour: '2-digit',
          minute: '2-digit',
        });
        return {
          checked: false,
          consultDate,
          time,
          value: current[type] ? current[type] : null,
          status: current[type + 'StatusLabel'] ? current[type + 'StatusLabel'] : null,
          statusText: current[type + 'StatusLabel'] ? this.translate.instant('misuration.label_graph.' + current[type + 'StatusLabel']) : null,
          course,
          action: 'Elimina',
          type,
          id: current.id,
        };
      });
    }
    return null;
  }

  openDeleteModal(data: any) {
    const modalRef: NzModalRef<ModalWithTwoButtonsComponent> = this.modal.create({
      nzContent: ModalWithTwoButtonsComponent,
      nzClosable: true,
      nzFooter: null,
      nzData: {
        dataModal: {
          texts: {
            title: this.translate.instant('misuration.modalTitle'),
            content: this.translate.instant('misuration.modalText') + this.translate.instant(`misuration.name_graphs.${data.type}`),
            first_button_pointer: 'delete',
          },
          colorActionButton: 'blue',
          actionButton: () => {
            this.deleteMisuration(data);
            modalRef.close();
          },
        },
      },
    });
  }

  public deleteMisuration(data: any) {
    const userData = JSON.parse(this.storage.getItem('userData')!) as UserAccountDTO;
    this.http.deleteMedicalStats('delete' + data.type, userData.individualId, data.id).subscribe({
      next: (_) => {
        this.tableData[data.type] = this.tableData[data.type].filter((res: any) => res.id !== data.id);
        this.updateGraphData(data.type);
        this.cdr.detectChanges();
        this.toastr.success(this.translate.instant('toastr.success.measurementSuccess'));
      },
    });
  }
}
